import {Box, Checkbox, CircularProgress, FormControlLabel} from "@mui/material";
import LookupAnalyticsLogsViewSearchSection, {clickStatusValues, reviewStatusValues} from "../../../../../../components/search-sections/lookup-analytics/logs";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import useQuery from "../../../../../../hooks/use-query";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import {LookupAnalyticsLogsDataGridColumnNames} from "../../../../../../../core/constants/table/data-grid-column-keys";
import {dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../../packages/data-grid";
import {DefaultPaginationInfo} from "../../../../../../../core/constants/enums";
import useSearch from "../../../../../../hooks/use-search";
import IconButton from "../../../../../../components/base/button/icon_button";
import {Link} from "react-router-dom";
import {routes} from "../../../../../../../core/models/static/routes/app-routes";
import {OpenInNew} from "@mui/icons-material";
import PartnerDataGrid from "../../../../../../containers/data-grid";
import DateUtils from "../../../../../../../core/services/date-utils";
import {stringifyUrl} from "query-string";
import LookupAppliedFiltersPopover from "../../../../../../components/app-specific/draggable-popover/lookup-applied-filters-popover";
import ProductInformationPopover from "../../../../../../components/app-specific/draggable-popover/product-information-popover";
import Api from "../../../../../../../core/services/api/api";
import {LookupAnalyticsDateOptionRanges} from "../../../../../../components/search-sections/lookup-analytics/dashboard";
import Button from "../../../../../../components/base/button/button";
import UpdateLookupAnalyticsReviewNoteDialog from "../../../../../../components/dialogs/update-lookup-analytics-review-note";

const lookupLogsExportChunkSize = 100;

const dataGridSaveConfig = {
    name: 'LookupAnalyticsLogsViewDataGrid',
    version: 2,
}

const initialReviewLogDialog = {open: false, log: null, onSuccess: () => void 0, close: () => void 0};

const LookupAnalyticsLogsView = () => {
    const [data, setData] = useState([]);
    const [reviewLogDialog, setReviewLogDialog] = useState(initialReviewLogDialog);
    const [toggling, setToggling] = useState([])
    const [query, setQuery] = useQuery();
    const operatorAccessLevels = useAccessLevels();
    const isMounted = useIsMounted();
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.analytics.lookup-logs'});

    const canExportLookupLogs = operatorAccessLevels.packages.dashboard.lookupLogs
        && operatorAccessLevels.packages.dashboard.exportLookupAnalyticsLogs;


    /**
     * Memo version of columns entities used in data grid
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                name: LookupAnalyticsLogsDataGridColumnNames.navigate,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                width: 0,
                sortable: false,
                resizable: false,
                pinned: true,
            },
            {
                title: "Filters Count",
                name: LookupAnalyticsLogsDataGridColumnNames.filters,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
            },
            {
                title: "Keyword",
                name: LookupAnalyticsLogsDataGridColumnNames.keyword,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: "User",
                name: LookupAnalyticsLogsDataGridColumnNames.user,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: "Customer",
                name: LookupAnalyticsLogsDataGridColumnNames.customer,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: "Operator",
                name: LookupAnalyticsLogsDataGridColumnNames.operator,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: "Clicked Part",
                name: LookupAnalyticsLogsDataGridColumnNames.clickedPart,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: "Result Count",
                name: LookupAnalyticsLogsDataGridColumnNames.resultCount,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
            },
            {
                title: "Submit Date Time",
                name: LookupAnalyticsLogsDataGridColumnNames.submitDateTime,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.dateTime,
                width: 120,
            },
            {
                title: "Lost Search Description",
                name: LookupAnalyticsLogsDataGridColumnNames.lostSearchDescription,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 160,
                sortable: false,
            },
            {
                title: "Reviewed",
                name: LookupAnalyticsLogsDataGridColumnNames.hasBeenReviewed,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
            },
            {
                title: "Review Note",
                name: LookupAnalyticsLogsDataGridColumnNames.reviewNote,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
            },
            {
                title: "Repeat Count",
                name: LookupAnalyticsLogsDataGridColumnNames.repeatCount,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
        ], [])

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Searches for logs by calling the api regarding this action
     * if the api call was successful, populates the [customers] in the inner state and updates the pagination info
     *
     * @type {(function(UseSearchFunctionArg, boolean=): Promise<undefined|ApiResponseDs<*>>)}
     */
    const fetchEntries = useCallback(async (args, forExport) => {
        const forApi = {
            sorts: args.orderBy?.field ? [{
                property: args.orderBy?.field,
                isDesc: args.orderBy?.descending,
            }] : [],
            pagination: args.paginationInfo,
            keyword: args.filters?.keyword ?? null,
            period: {
                startDateTime: args.filters.period?.startDateTime,
                endDateTime: args.filters.period?.endDateTime,
            },
            industryId: args.filters.industryId,
            operatorId: args.filters.operator?.id,
            customerId: args.filters.customer?.id ? Number(args.filters.customer?.id) : null,
            partId: args.filters.part?.id ? Number(args.filters.part?.id) : null,
            hasClicked: args.filters?.clickStatus === clickStatusValues.clicked ? true : args.filters?.clickStatus === clickStatusValues.notClicked ? false : null,
            hasReviewed: args.filters?.reviewStatus === reviewStatusValues.reviewed ? true : args.filters?.reviewStatus === reviewStatusValues.notReviewed ? false : null,
        }

        const defaultPeriod = LookupAnalyticsDateOptionRanges.last2Weeks();
        defaultPeriod.startDate = DateUtils.UTCToServer(defaultPeriod.startDate).format(DateUtils.withoutOffsetFormat);
        defaultPeriod.endDate = DateUtils.UTCToServer(defaultPeriod.endDate).format(DateUtils.withoutOffsetFormat);

        forApi.period = {
            ...forApi.period,
            startDateTime: forApi.period?.startDateTime ?? defaultPeriod.startDate,
            endDateTime: forApi.period?.endDateTime ?? defaultPeriod.endDate,
        }

        const response = await Api.getNexusSearchReportNexusLogs(forApi, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (response?.resultFlag && !forExport) {
            setData(response.data.items);
            args.setPaginationInfo(prevState => ({
                ...prevState,
                length: response?.data?.totalCount ?? 0
            }));
        }
        if (response?.resultFlag && forExport) {
            return response;
        }
    }, [isMounted])

    /**
     * Searches among the entries based on the filters from the user.
     * @type {UseSearchFunction}
     */
    const search = useCallback(async (result) => {
        await fetchEntries(result, false);
    }, [fetchEntries]);

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize,
        filters: {...query},
    } : DefaultPaginationInfo, [dataGridPageSize, query])

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
    }), [initialPaginationInfo, dataGridSortBy]);

    const searchData = useSearch(search, initialSearchDataSettings, false)
    const {setFilters} = searchData;

    /**
     * When the filters change, update the search data
     */
    useEffect(() => {
        setFilters({...query})
    }, [setFilters, query]);

    const exportColumns = useMemo(() => ({
        filtersCount: t('columns.filtersCount'),
        keyword: t('columns.keyword'),
        user: t('columns.user'),
        customer: t('columns.customer'),
        operator: t('columns.operator'),
        clickedPart: t('columns.clickedPart'),
        resultCount: t('columns.resultCount'),
        submitDateTime: t('columns.submitDateTime'),
        lostSearchDescription: t('columns.lostSearchDescription'),
        reviewed: t('columns.reviewed'),
        reviewNote: t('columns.reviewNote'),
        repeatCount: t('columns.repeatCount'),
        reviewedYes: t('reviewedYes'),
        reviewedNo: t('reviewedNo'),
    }), [t]);

    /**
     * Exports all lookup logs matching current filters to CSV.
     */
    const exportLookupLogsToCsv = useCallback(async () => {
        const totalCount = searchData.paginationInfo?.length ?? 0;
        const paginationInfo = {
            length: totalCount,
            pageSize: lookupLogsExportChunkSize,
            currentPage: 1,
            sizes: searchData.paginationInfo?.sizes ?? DefaultPaginationInfo.sizes,
        };
        const logs = [];

        while (totalCount > logs.length) {
            const response = await fetchEntries({...searchData, paginationInfo}, true);
            const items = response?.data?.items ?? [];
            if (!response?.resultFlag) {
                return;
            }
            if (!items.length) {
                break;
            }
            logs.push(...items);
            paginationInfo.currentPage = (paginationInfo.currentPage ?? 1) + 1;
        }

        return {
            title: `${t('exportTitle')} - ${DateUtils.localNow().format(DateUtils.dateTimeFormat)}`,
            body: logs.map((log) => ({
                [exportColumns.filtersCount]: log.filters?.length ?? 0,
                [exportColumns.keyword]: log.keyword ?? '',
                [exportColumns.user]: log.userName ?? '',
                [exportColumns.customer]: log.customerName ?? '',
                [exportColumns.operator]: log.partnerStaffName ?? '',
                [exportColumns.clickedPart]: log.partNumber ?? '',
                [exportColumns.resultCount]: log.foundedItem ?? '',
                [exportColumns.submitDateTime]: log.submitDateTime
                    ? DateUtils.serverToLocal(log.submitDateTime).format(DateUtils.dateTimeFormat)
                    : '',
                [exportColumns.lostSearchDescription]: log.lostSearchDescription ?? '',
                [exportColumns.reviewed]: log.hasBeenReviewed
                    ? exportColumns.reviewedYes
                    : exportColumns.reviewedNo,
                [exportColumns.reviewNote]: log.reviewNote ?? '',
                [exportColumns.repeatCount]: log.repeatCount ?? '',
            })),
        };
    }, [exportColumns, fetchEntries, searchData, t]);

    /**
     * Toggles the has-been-reviewed status of the entry.
     * @type {(function(*, *): Promise<void>)}
     */
    const toggleChecked = useCallback(async (id, value) => {
        setToggling(prevState => [...prevState, id]);
        const response = await Api.toggleNexusLogReview({id: id});
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(prevState => prevState.map(e => e.id === id ? {...e, hasBeenReviewed: value} : e));
        }
        setToggling(prevState => prevState.filter(e => e !== id));
    }, [isMounted])

    /**
     * Opens the review note dialog
     * @type {(function(*))}
     */
    const openEditReviewNote = useCallback((log) => {
        setReviewLogDialog({
            open: true,
            log: log,
            onSuccess: (props) => setData(prevState => prevState.map(e => e.id === log.id ? {...e, ...props} : e)),
            close: () => setReviewLogDialog(initialReviewLogDialog),
        });
    }, [])

    /**
     * Memo version of table row records used in data grid
     */
    const rows = useMemo(() => {
        return data?.map(log => ({
            key: log.id,
            data: log,
            cells: {
                [LookupAnalyticsLogsDataGridColumnNames.navigate]: operatorAccessLevels.packages.lookup.nexusSearch &&
                <IconButton
                    color={'secondary'}
                    component={Link}
                    to={stringifyUrl({
                        url: routes.lookup.search,
                        query: {
                            searchToken: log.id,
                            readonly: true,
                        },
                    })}
                >
                    <OpenInNew className='icon normal'/>
                </IconButton>,
                [LookupAnalyticsLogsDataGridColumnNames.filters]:
                    <LookupAppliedFiltersPopover
                        filters={log.filters}
                        title={`Searched By ${log.userName ?? log.customerName ?? log.partnerStaffName ?? ""} at ${DateUtils.serverToLocal(log.submitDateTime).format(DateUtils.dateFormatAlternative)}`}
                    >
                        {log.filters.length ?? 0}
                    </LookupAppliedFiltersPopover>,
                [LookupAnalyticsLogsDataGridColumnNames.keyword]: log.keyword,
                [LookupAnalyticsLogsDataGridColumnNames.user]: log.userName,
                [LookupAnalyticsLogsDataGridColumnNames.customer]: log.customerName,
                [LookupAnalyticsLogsDataGridColumnNames.operator]: log.partnerStaffName,
                [LookupAnalyticsLogsDataGridColumnNames.clickedPart]: log.partNumber
                    ? <ProductInformationPopover partNo={log.partNumber}>{log.partNumber ?? ""}</ProductInformationPopover>
                    : log.partNumber,
                [LookupAnalyticsLogsDataGridColumnNames.resultCount]: log.foundedItem,
                [LookupAnalyticsLogsDataGridColumnNames.submitDateTime]: log.submitDateTime,
                [LookupAnalyticsLogsDataGridColumnNames.lostSearchDescription]: log.lostSearchDescription,
                [LookupAnalyticsLogsDataGridColumnNames.hasBeenReviewed]:
                    toggling.includes(log.id)
                        ? <div className={'p-2'}>
                            <CircularProgress size={15}/>
                        </div>
                        : <FormControlLabel
                            sx={{margin: 0}}
                            control={
                                <Checkbox
                                    disabled={toggling.includes(log.id)}
                                    sx={{paddingBlock: 0.75}}
                                    disableRipple
                                    checked={log?.hasBeenReviewed}
                                    onChange={(e, checked) => toggleChecked(log.id, checked)}
                                />
                            }
                        />,
                [LookupAnalyticsLogsDataGridColumnNames.repeatCount]: log.repeatCount,
                [LookupAnalyticsLogsDataGridColumnNames.reviewNote]:
                    <div>
                        <p {...dataGridCellTextProps} className={'word-line-break-anywhere'}>
                            {
                                log.reviewNote
                                    ? log.reviewNote?.toString()?.substring(0, Math.min(160, log.reviewNote?.length ?? 0))
                                    + ((log.reviewNote?.length ?? 0) > 160 ? '...' : '')
                                    : ''
                            }
                        </p>
                        <Button
                            className={`m-1 d-inline-block`}
                            variant={'outlined'}
                            color={'primary'}
                            onClick={() => openEditReviewNote(log)}
                        >
                            {
                                !!log.reviewNote
                                    ? "View"
                                    : "Create"
                            }
                        </Button>
                    </div>
            }
        })) ?? []
    }, [openEditReviewNote, data, operatorAccessLevels.packages.lookup.nexusSearch, toggleChecked, toggling])


    return (
        <>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                <LookupAnalyticsLogsViewSearchSection filters={query} setFilters={setQuery}/>
                <PartnerDataGrid
                    {...dataGridSaveConfig}
                    initialLayoutState={{columns: columns}}
                    columns={dataGridColumns}
                    rows={rows}
                    hideFooterActions
                    getExportInfo={canExportLookupLogs ? exportLookupLogsToCsv : undefined}
                    state={{
                        sortBy: searchData.orderBy,
                        pagination: searchData.paginationInfo,
                        loading: {state: searchData.loading},
                        density: dataGridDensity,
                    }}
                    onSortByChanged={(_, current) => searchData.setOrderBy(current)}
                    onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                    onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                />
            </Box>
            <UpdateLookupAnalyticsReviewNoteDialog
                open={reviewLogDialog.open}
                log={reviewLogDialog.log}
                close={reviewLogDialog.close}
                onSuccess={reviewLogDialog.onSuccess}
            />
        </>
    );
}

export default LookupAnalyticsLogsView;